<script lang="ts" setup>
import {ExtendedProject, GpgPublicKey, Role, SshPublicKey} from "@/types";
import ProjectLayout from "@/Pages/projects/layouts/ProjectLayout.vue";
import BaseLayout from "@/layouts/BaseLayout.vue";
import {Head} from "@inertiajs/vue3";
import {useDate} from "@/composables/date";
import CopyBtn from "@/components/buttons/CopyBtn.vue";

interface Props {
    project: ExtendedProject,
    role: Role,
    sshKeys: Array<SshPublicKey>
    gpgKeys: Array<GpgPublicKey>
}

const props = defineProps<Props>()
defineOptions({
    layout: BaseLayout
})

const {shortDate} = useDate()
</script>

<template>
    <Head title="SSH & PGP keys" />
    <project-layout
        :role="props.role"
        :project="props.project"
    >
        <h1 class="font-weight-medium text-h5 mt-5">
            SSH keys
        </h1>
        <v-divider class="my-5" />
        <p
            v-if="sshKeys.length === 0"
            class="text-body-2"
        >
            There are no available SSH keys.
        </p>
        <div v-else>
            <p class="text-body-2">
                This is a list of SSH keys from users who have access to this project.
            </p>
            <div class="font-weight-bold mt-3 mb-1">
                Authentication keys
            </div>
            <v-list
                rounded="lg"
                border="sm"
            >
                <v-list-item
                    v-for="(sshKey, index) in props.sshKeys"
                    :key="sshKey.hash"
                    :class="index < props.sshKeys.length - 1 ? 'border-b' : null"
                    :slim="true"
                    class="py-3"
                >
                    <template
                        #prepend
                    >
                        <v-row>
                            <v-col
                                cols="12"
                                class="pa-0"
                            >
                                <v-icon
                                    class="text-center ml-8"
                                    color="black"
                                    size="36"
                                >
                                    mdi-key-outline
                                </v-icon>
                            </v-col>
                            <v-col
                                cols="12"
                                class="pa-0 text-center"
                            >
                                <span class="border-sm rounded-lg pa-1 text-caption text-grey-darken-2">SSH</span>
                            </v-col>
                        </v-row>
                    </template>
                    <v-list-item-title
                        class="font-weight-bold text-body-2"
                    >
                        {{ sshKey.user }}
                    </v-list-item-title>
                    <v-list-item-title class="text-body-2">
                        <code
                            class="text-black"
                            style="font-size: 12px;"
                        >SHA256:{{ sshKey.hash }}</code>
                    </v-list-item-title>
                    <v-list-item-subtitle>
                        Added on {{ shortDate(sshKey.created_at) }}
                    </v-list-item-subtitle>
                    <template #append>
                        <copy-btn :text="sshKey.public_key" />
                    </template>
                </v-list-item>
            </v-list>
        </div>

        <h1 class="font-weight-medium text-h5 mt-15">
            PGP keys
        </h1>
        <v-divider class="my-5" />
        <p
            v-if="gpgKeys.length === 0"
            class="text-body-2"
        >
            There are no available PGP keys.
        </p>
        <div v-else>
            <p class="text-body-2">
                This is a list of PGP keys from users who have access to this project.
            </p>
            <div class="font-weight-bold mt-3 mb-1">
                Signing keys
            </div>
            <v-list
                rounded="lg"
                border="sm"
            >
                <v-list-item
                    v-for="(gpgKey, index) in props.gpgKeys"
                    :key="gpgKey.key_id"
                    :class="index < props.gpgKeys.length - 1 ? 'border-b' : null"
                    :slim="true"
                    class="pt-1 pb-2"
                >
                    <template
                        #prepend
                    >
                        <v-row>
                            <v-col
                                cols="12"
                                class="pa-0"
                            >
                                <v-icon
                                    class="text-center ml-8"
                                    color="black"
                                    size="36"
                                >
                                    mdi-key-outline
                                </v-icon>
                            </v-col>
                            <v-col
                                cols="12"
                                class="pa-0 text-center"
                            >
                                <span class="border-sm rounded-lg pa-1 text-caption text-grey-darken-2">PGP</span>
                            </v-col>
                        </v-row>
                    </template>
                    <v-list-item-title
                        class="font-weight-bold text-body-2"
                    >
                        {{ gpgKey.user }}
                    </v-list-item-title>
                    <v-list-item-title
                        class="font-weight-bold text-body-2"
                    >
                        {{ gpgKey.email_addresses[0] }}
                    </v-list-item-title>
                    <v-list-item-title
                        class="font-weight-bold text-body-2 pb-1"
                    >
                        Key ID: <code
                            class="font-weight-regular"
                        >{{ gpgKey.key_id }}</code>
                    </v-list-item-title>
                    <v-list-item-title
                        class="font-weight-bold text-body-2 pb-1"
                    >
                        Subkeys: <code
                            class="font-weight-regular"
                        >{{ gpgKey.sub_keys.toString() }}</code>
                    </v-list-item-title>
                    <v-list-item-subtitle>
                        Added on {{ shortDate(gpgKey.created_at) }}
                    </v-list-item-subtitle>

                    <template #append>
                        <copy-btn
                            :text="gpgKey.public_key"
                        />
                    </template>
                </v-list-item>
            </v-list>
        </div>
    </project-layout>
</template>

<style scoped>
</style>
